export interface MockPayor {
  id: number;                    
  contactId: number;             
  userId: number;
  name: string;                  
  email: string;                 
  isGuarantor: boolean;         
  address: string;
  type: 'patient' | 'champion';
}

export interface MockUserPaymentMethod {
  paymentMethodId: string; 
  stripePaymentMethodId: string; 
  stripeCustomerId: string; 
  extPaymentUserId?: string; // Stripe customer ID from payment method
  paymentMethodType: 'card' | 'us_bank_account';
  last4: string;
  brand?: string; 
  expMonth?: number;
  expYear?: number;
  bankName?: string;
  isDefault: boolean;
  isActive: boolean;
  nameOnMethod: string;
  createdAt: string;
}

export interface MockUser {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  stripeCustomerId: string | null;
  isPatient: boolean;
  isActive: boolean;
  paymentMethods: MockUserPaymentMethod[];
}

export interface MockUserLookupResult {
  exists: boolean;
  user: MockUser | null;
  savedPaymentMethods: MockUserPaymentMethod[];
}

export const NEW_PAYOR_ID = 'NEW_PAYOR';

export const mockPayors: MockPayor[] = [
  {
    id: 12345,
    contactId: 67890,
    userId: 1001,
    name: 'John Doe',
    email: 'john.doe@example.com',
    isGuarantor: true,
    address: '123 Main Street, Springfield, IL 62701',
    type: 'patient',
  },
  {
    id: 23456,
    contactId: 78901,
    userId: 1002,
    name: 'Sarah Smith',
    email: 'sarah.smith@example.com',
    isGuarantor: true,
    address: '456 Oak Avenue, Chicago, IL 60601',
    type: 'champion',
  },
  {
    id: 34567,
    contactId: 89012,
    userId: 1003,
    name: 'Michael Johnson',
    email: 'michael.johnson@example.com',
    isGuarantor: false,
    address: '789 Pine Road, Naperville, IL 60540',
    type: 'champion',
  },
  {
    id: 45678,
    contactId: 90123,
    userId: 0,
    name: 'Emily Davis',
    email: 'emily.davis@newuser.com',
    isGuarantor: false,
    address: '321 Elm Street, Evanston, IL 60201',
    type: 'champion',
  },
];


export const mockLookupUserByEmail = async (email: string): Promise<MockUserLookupResult> => {
  await new Promise(resolve => setTimeout(resolve, 300));
  
  // Return empty result - user lookup should be handled by actual API
  return {
    exists: false,
    user: null,
    savedPaymentMethods: [],
  };
};

export const mockBatchLookupUsers = async (
  emails: string[]
): Promise<Record<string, MockUserLookupResult>> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 400));
  
  const results: Record<string, MockUserLookupResult> = {};
  
  for (const email of emails) {
    const normalizedEmail = email.toLowerCase().trim();
    // Return empty result - user lookup should be handled by actual API
    results[normalizedEmail] = {
      exists: false,
      user: null,
      savedPaymentMethods: [],
    };
  }
  
  return results;
};

const mockCreateUser = async (
  email: string,
  firstName: string,
  lastName: string
): Promise<MockUser> => {
  await new Promise(resolve => setTimeout(resolve, 500));
  
  // Return mock user - actual user creation should be handled by actual API
  return {
    userId: `user_${Date.now()}`,
    email: email.toLowerCase().trim(),
    firstName,
    lastName,
    stripeCustomerId: null,
    isPatient: false,
    isActive: true,
    paymentMethods: [],
  };
};

export const mockCreateStripeCustomer = async (
  userId: string,
  email: string,
  name: string
): Promise<string> => {
  await new Promise(resolve => setTimeout(resolve, 400));
  
  // Return mock Stripe customer ID - actual creation should be handled by actual API
  return `cus_${Date.now()}`;
};

export const mockSavePaymentMethod = async (
  userId: string,
  stripeCustomerId: string,
  stripePaymentMethodId: string,
  paymentMethodDetails: {
    type: 'card' | 'us_bank_account';
    last4: string;
    brand?: string;
    expMonth?: number;
    expYear?: number;
    bankName?: string;
    nameOnMethod: string;
  }
): Promise<MockUserPaymentMethod> => {
  await new Promise(resolve => setTimeout(resolve, 300));
  
  // Return mock payment method - actual saving should be handled by actual API
  return {
    paymentMethodId: `upm_${Date.now()}`,
    stripePaymentMethodId,
    stripeCustomerId,
    paymentMethodType: paymentMethodDetails.type,
    last4: paymentMethodDetails.last4,
    brand: paymentMethodDetails.brand,
    expMonth: paymentMethodDetails.expMonth,
    expYear: paymentMethodDetails.expYear,
    bankName: paymentMethodDetails.bankName,
    nameOnMethod: paymentMethodDetails.nameOnMethod,
    isDefault: false,
    isActive: true,
    createdAt: new Date().toISOString(),
  };
};

export const formatPaymentMethodDisplay = (method: MockUserPaymentMethod): string => {
  if (method.paymentMethodType === 'card') {
    const brand = method.brand 
      ? method.brand.charAt(0).toUpperCase() + method.brand.slice(1) 
      : 'Card';
    return `${brand} •••• ${method.last4}`;
  } else {
    return `${method.bankName || 'Bank'} •••• ${method.last4}`;
  }
};

export const formatExpiration = (method: MockUserPaymentMethod): string | null => {
  if (method.paymentMethodType === 'card' && method.expMonth && method.expYear) {
    return `${method.expMonth.toString().padStart(2, '0')}/${method.expYear}`;
  }
  return null;
};

export const getPaymentMethodIcon = (method: MockUserPaymentMethod): string => {
  if (method.paymentMethodType === 'us_bank_account') {
    return 'bank';
  }
  
  switch (method.brand?.toLowerCase()) {
    case 'visa':
      return 'visa';
    case 'mastercard':
      return 'mastercard';
    case 'amex':
      return 'amex';
    case 'discover':
      return 'discover';
    default:
      return 'card';
  }
};

export const mockPatientData = {
  patientId: 12345,
  contact: {
    contactId: 67890,
    firstName: 'John',
    lastName: 'Doe',
    email: 'john.doe@example.com',
    primPhoneNum: '555-123-4567',
    primaryAddress: {
      streetAddress1: '123 Main Street',
      streetAddress2: '',
      city: 'Springfield',
      state: 'IL',
      zipCode: '62701',
    },
  },
};

export interface MockRecentPatient {
  patientId: number;
  firstName: string;
  lastName: string;
  dob: string;
}

export const mockRecentPatients: Record<number, MockRecentPatient> = {
  12345: {
    patientId: 12345,
    firstName: 'John',
    lastName: 'Doe',
    dob: '1985-03-15',
  },
  23456: {
    patientId: 23456,
    firstName: 'Sarah',
    lastName: 'Smith',
    dob: '1990-07-22',
  },
  34567: {
    patientId: 34567,
    firstName: 'Michael',
    lastName: 'Johnson',
    dob: '1988-11-08',
  },
  45678: {
    patientId: 45678,
    firstName: 'Emily',
    lastName: 'Davis',
    dob: '1992-05-30',
  },
  56789: {
    patientId: 56789,
    firstName: 'Robert',
    lastName: 'Wilson',
    dob: '1983-09-12',
  },
  67890: {
    patientId: 67890,
    firstName: 'Jessica',
    lastName: 'Martinez',
    dob: '1995-01-25',
  },
  78901: {
    patientId: 78901,
    firstName: 'David',
    lastName: 'Anderson',
    dob: '1987-12-03',
  },
  89012: {
    patientId: 89012,
    firstName: 'Amanda',
    lastName: 'Taylor',
    dob: '1991-06-18',
  },
};

export const mockEncounterData = {
  patientEncounterId: 98765,
  patientId: 12345,
  clientId: 100093,
  facilityId: 104056,
  pfrAmt: 5000,
  patientPaymentProgram: [
    {
      patientPaymentProgramId: 111,
      patientPaymentSchedule: {
        patientPaymentScheduleId: 222,
        pfrAmt: 5000,
        pfrBalance: 3500,
        pfrPendingBalance: 3500,
        paymentDueAmt: 350,
        terms: 10,
        remainingTerms: 8,
        paymentFreq: 'M',
        scheduleStatus: 'Active',
      },
    },
  ],
  patientChampion: [
    {
      patientChampionId: 23456,
      isGuarantor: true,
      contact: {
        contactId: 78901,
        firstName: 'Sarah',
        lastName: 'Smith',
        email: 'sarah.smith@example.com',
        primPhoneNum: '555-234-5678',
        primaryAddress: {
          streetAddress1: '456 Oak Avenue',
          streetAddress2: 'Apt 2B',
          city: 'Chicago',
          state: 'IL',
          zipCode: '60601',
        },
      },
    },
    {
      patientChampionId: 34567,
      isGuarantor: false,
      contact: {
        contactId: 89012,
        firstName: 'Michael',
        lastName: 'Johnson',
        email: 'michael.johnson@example.com',
        primPhoneNum: '555-345-6789',
        primaryAddress: {
          streetAddress1: '789 Pine Road',
          streetAddress2: '',
          city: 'Naperville',
          state: 'IL',
          zipCode: '60540',
        },
      },
    },
    {
      patientChampionId: 45678,
      isGuarantor: false,
      contact: {
        contactId: 90123,
        firstName: 'Emily',
        lastName: 'Davis',
        email: 'emily.davis@newuser.com',
        primPhoneNum: '555-456-7890',
        primaryAddress: {
          streetAddress1: '321 Elm Street',
          streetAddress2: '',
          city: 'Evanston',
          state: 'IL',
          zipCode: '60201',
        },
      },
    },
  ],
};

export default {
  mockPayors,
  mockPatientData,
  mockEncounterData,
  mockRecentPatients,
  
  mockLookupUserByEmail,
  mockBatchLookupUsers,
  mockCreateUser,
  mockCreateStripeCustomer,
  mockSavePaymentMethod,
  
  formatPaymentMethodDisplay,
  formatExpiration,
  getPaymentMethodIcon,
};

